import type { FC } from 'react';
import { useEffect } from 'react';
import { z } from 'zod';
import type { Tsos } from 'src/apis/capacity-management/types';
import { SearchableMultiSelect } from 'src/components/form/select/searchable-multi-select';
import { formOptionSchema } from 'src/components/form/zod-schemas';
import { useAxios } from 'src/hooks/use-axios';
import { useClientSearch } from 'src/hooks/use-client-search';

type Props = {
  label: string;
  url: string;
  placeholder: string;
  name?: string;
};

export const tsoOptionSchema = z.object({
  shortName: z.string(),
  ...formOptionSchema.shape,
});

type TsoOption = z.input<typeof tsoOptionSchema>;

export const TsoSearchableMultiSelect: FC<Props> = ({
  label,
  url,
  placeholder,
  name = 'tsoId',
}) => {
  const searchRequest = useAxios(
    (axios, baseConfig) =>
      axios.request<Tsos>({
        ...baseConfig,
        url,
      }),
    { neededOnPageLoad: false }
  );

  const tsos = searchRequest.response?.data._embedded.items.map(
    (tso) =>
      ({
        value: tso.tsoId,
        label: tso.name,
        shortName: tso.tsoShortName,
      }) satisfies TsoOption
  );

  const search = useClientSearch(
    tsos,
    (tso, search) =>
      tso.label.toLowerCase().match(search) ||
      tso.shortName.toLowerCase().match(search)
  );

  // on the very first search input we try to fetch the initial list
  useEffect(() => {
    if (!search.searchValue || searchRequest.response) return;
    searchRequest.execute();
    // oxlint-disable-next-line exhaustive-deps
  }, [search.searchValue, searchRequest.response]);

  return (
    <SearchableMultiSelect
      label={label}
      name={name}
      stacked
      placeholder={placeholder}
      {...search}
      renderOptionHint={(option) => option.shortName}
      renderSelectedOptionTooltip={(option) => (
        <>
          <strong>Short Name:</strong> {option.shortName}
        </>
      )}
    />
  );
};
